<template>
    <div id="tip" class="content-spacer">
        <ViewHeader title="Add Tip" />
        <div id="rest-of-body" class="drop-shadow-sides">
            <div id="total">
                <table>
                    <tr>
                        <td class="label-column">Total</td>
                        <td class="value-column">{{ total | toCurrency }}</td>
                    </tr>
                    <tr>
                        <td class="label-column">Tip</td>
                        <td class="value-column">
                            <transition :name="readyForAnimation ? 'amount-drop-in' : ''" mode="out-in">
                                <div :key="enteredTip">
                                    {{ enteredTip | toCurrency }}
                                </div>
                            </transition>
                        </td>
                    </tr>
                </table>
            </div>
            <div id="actions">
                <button @click="select('18-pct-tip')" class="button-secondary"
                        :class="{'button-secondary-selected': selected === '18-pct-tip'}">
                    18%
                </button>
                <button @click="select('20-pct-tip')" class="button-secondary"
                        :class="{'button-secondary-selected': selected === '20-pct-tip'}">
                    20%
                </button>
                <button @click="select('25-pct-tip')" class="button-secondary"
                        :class="{'button-secondary-selected': selected === '25-pct-tip'}">
                    25%
                </button>

                <Money v-if="isOtherPctTipInputVisible" v-model="formattedOtherPctTip"
                       v-bind="percentage" @blur.native="setOtherPctTip"
                       @input.native="scrubNumberInput" @keyup.native="validateAndSetOtherPctTipOnEnter"
                       v-focus :class="otherPctTipClass" />
                <button v-else class="button-secondary" @click="editOtherPctTip"
                        :class="{'button-secondary-selected': selected === 'other-pct-tip'}">
                    <span v-if="otherPctTip > 0">{{ otherPctTip | toFormattedNumber(0) }}%</span>
                    <span v-else>Other %</span>
                </button>

                <Money v-if="isDollarTipInputVisible" v-model="formattedDollarTip"
                       v-bind="money" @blur.native="setDollarTip"
                       @input.native="scrubNumberInput" @keyup.native="validateAndSetDollarTipOnEnter"
                       v-focus :class="dollarTipClass" />
                <button v-else class="button-secondary" @click="editDollarTip"
                        :class="{'button-secondary-selected': selected === 'dollar-tip'}">
                    {{ (dollarTip > 0 ? dollarTip : '$') | toCurrency }} Tip
                </button>

                <button @click="select('no-tip')" class="button-secondary"
                        :class="{'button-secondary-selected': selected === 'no-tip'}">
                    No Tip
                </button>
                <button @click="commitTip" :disabled="isConfirmDisabled"
                        class="button-primary">
                    Confirm
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import ViewHeader from "../components/ViewHeader";
    import { Money } from "v-money";
    import { mapState, mapGetters, mapMutations } from "vuex";
    import { sweetAlert2 } from "../mixins/sweet-alert2";
    import toCurrency from "../filters/to-currency";

    export default {
        name: "Tip",
        mixins: [sweetAlert2],
        components: {
            ViewHeader,
            Money
        },
        data: function () {
            return {
                readyForAnimation: false,
                selected: "",
                previouslySelected: "",
                customInputRollbackValue: 0,
                otherPctTip: 0,
                formattedOtherPctTip: "",
                isOtherPctTipInputVisible: false,
                dollarTip: 0,
                formattedDollarTip: "",
                isDollarTipInputVisible: false,
                enteredTip: 0,
                promptToConfirmLeave: true,
                money: {
                    decimal: ".",
                    thousands: ",",
                    prefix: "$",
                    precision: 2,
                    masked: true
                },
                percentage: {
                    decimal: ".",
                    thousands: ",",
                    suffix: "%",
                    precision: 0,
                    masked: true
                }
            }
        },
        computed: {
            ...mapState({
                tipFromState: state => state.session.tip
            }),
            ...mapGetters([
                "total"
            ]),
            isConfirmDisabled() {
                return this.isOtherPctTipInputVisible || this.isDollarTipInputVisible;
            },
            otherPctTipClass() {
                const borderClass = this.formattedOtherPctTip !== "0%" ? "input-valid" : "input-invalid"
                return `input-secondary ${borderClass}`;
            },
            dollarTipClass() {
                const borderClass = this.formattedDollarTip !== "$0.00" ? "input-valid" : "input-invalid"
                return `input-secondary ${borderClass}`;
            }
        },
        watch: {
            enteredTip: function (value) {
                this.setTempTip(value);
            }
        },
        methods: {
            ...mapMutations([
                "setTip",
                "setTempTip"
            ]),
            select(id) {
                let pct = this.getTipPctById(id);
                let percentage = Number(pct);

                this.previouslySelected = this.selected;
                this.selected = id;
                this.isDollarTipInputVisible = false;
                this.isOtherPctTipInputVisible = false;
                this.enteredTip = isNaN(percentage) ? 0 : this.total * (pct / 100);
                this.resetCustomValue();
            },
            editOtherPctTip() {
                this.previouslySelected = this.selected;
                this.selected = 'other-pct-tip';
                this.isDollarTipInputVisible = false;
                this.isOtherPctTipInputVisible = true;
                this.customInputRollbackValue = this.otherPctTip;
                this.formattedOtherPctTip = this.otherPctTip;
            },
            editDollarTip() {
                this.previouslySelected = this.selected;
                this.selected = 'dollar-tip';
                this.isOtherPctTipInputVisible = false;
                this.isDollarTipInputVisible = true;
                this.customInputRollbackValue = this.dollarTip;
                this.formattedDollarTip = this.dollarTip;
            },
            setOtherPctTip() {
                // We set the value whether it's losing focus or the enter key is hit, as
                // long as the value is valid. Otherwise, we will rollback.
                if (this.formattedOtherPctTip !== "0%") {
                    this.otherPctTip = this.toNumber(this.formattedOtherPctTip);
                    this.isOtherPctTipInputVisible = false;
                    this.enteredTip = this.total * (this.otherPctTip / 100);
                    this.dollarTip = 0;
                }
                else {
                    this.isOtherPctTipInputVisible = false;
                    this.otherPctTip = this.customInputRollbackValue; // rollback
                    this.selected = this.previouslySelected;
                }
            },
            validateAndSetOtherPctTipOnEnter(e) {
                // Prevent the number from getting bigger than 999%
                if (this.formattedOtherPctTip.length > 4) {
                    this.formattedOtherPctTip = this.formattedOtherPctTip.substring(0, 4);
                }

                if (e.keyCode === 13) {
                    this.setOtherPctTip();
                }
            },
            setDollarTip() {
                // We set the value whether it's losing focus or the enter key is hit, as
                // long as the value is valid. Otherwise, we will rollback.
                if (this.formattedDollarTip !== "$0.00") {
                    this.dollarTip = this.toNumber(this.formattedDollarTip);
                    this.isDollarTipInputVisible = false;
                    this.enteredTip = this.dollarTip * 1; // Convert string to number.
                    this.otherPctTip = 0;
                }
                else {
                    this.isDollarTipInputVisible = false;
                    this.dollarTip = this.customInputRollbackValue; // rollback
                    this.selected = this.previouslySelected;
                }
            },
            validateAndSetDollarTipOnEnter(e) {
                // Prevent the number from getting bigger than $99,999.99.
                if (this.formattedDollarTip.length > 10) {
                    this.formattedDollarTip = this.formattedDollarTip.substring(0, 10);
                }

                if (e.keyCode === 13) {
                    this.setDollarTip();
                }
            },
            commitTip() {
                this.setTip(
                    {
                        tip: this.enteredTip,
                        selectedTipOption: this.selected
                    });

                this.promptToConfirmLeave = false;
                this.$router.go(-1);
            },
            resetCustomValue() {
                this.otherPctTip = 0;
                this.dollarTip = 0;
                this.customInputRollbackValue = 0;
            },
            getTipPctById(id) {
                switch (id) {
                    case "18-pct-tip": return 18;
                    case "20-pct-tip": return 20;
                    case "25-pct-tip": return 25;
                    default: return 0;
                }
            },
            toNumber(value) {
                return value.replace("$", "").replace("%", "").replace(/,/g, "");
            },
            // Had to hook to input event to not allow negative character.
            // Keydown event produces keyCode(229) and value(Unidentified) in android
            // and does not allow e.preventDefault() to cancel.
            scrubNumberInput(e) {
                const scrubbedInput = e.target.value.replace("-", "");

                if (e.target.value.indexOf("$") != -1) {
                    this.formattedDollarTip = scrubbedInput;
                }
                else {
                    this.formattedOtherPctTip = scrubbedInput;
                }
            }
        },
        mounted() {
            let selectedOption = this.$store.state.session.selectedTipOption;

            if (selectedOption === null) {
                this.select("18-pct-tip");
            }
            else {
                let committedTip = this.tipFromState;

                if (selectedOption === "other-pct-tip") {
                    this.otherPctTip = (committedTip / this.total) * 100;
                    this.formattedOtherPctTip = this.otherPctTip.toFixed(0);
                    this.selected = selectedOption;
                    this.setOtherPctTip();
                }
                else if (selectedOption === "dollar-tip") {
                    this.dollarTip = committedTip;
                    this.formattedDollarTip = this.dollarTip.toFixed(2);
                    this.selected = selectedOption;
                    this.setDollarTip();
                }
                else {
                    this.select(selectedOption);
                }
            }

            // We don't want "change" animation to run when we're still initializing.
            setTimeout(() => this.readyForAnimation = true, 100);
        },
        beforeRouteLeave(to, from, next) {
            if (this.promptToConfirmLeave &&
                this.tipFromState !== this.enteredTip) {

                this.swalConfirm(() => {
                    this.setTip(
                        {
                            tip: this.enteredTip,
                            selectedTipOption: this.selected
                        });
                }, `Set tip amount to ${toCurrency(this.enteredTip)}?`, "Confirm tip amount", "Yes", "No");
            }

            next();
        }
    }
</script>

<style scoped>
    #tip {
        display: flex;
        flex-direction: column;
    }

    #rest-of-body {
        flex-grow: 1;
        background-color: white;
        padding-bottom: 60px;
    }

    #total {
        background-color: var(--very-light-gray);
        padding: 10px;
        border-bottom: var(--border-gray) solid 1px;
    }

    #actions {
        background-color: var(--white);
        padding: 20px;
    }

    #actions > button {
        display: block;
        width: 100%;
        margin-bottom: 10px;
    }

    #actions > input {
        width: 100%;
        margin-bottom: 10px;
        text-align: center;
        box-sizing: border-box;
        -webkit-box-sizing: border-box;
        -moz-box-sizing: border-box;
    }
</style>